export const HASH_ALGORITHM = "SHA-256";

export const createHash = async (message: string): Promise<string> => {
  // using mdn approach: https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest#converting_a_digest_to_a_hex_string
  const msgUint8 = new TextEncoder().encode(message);
  const hashBuffer = await window.crypto.subtle.digest(
    HASH_ALGORITHM,
    msgUint8
  );
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return hashHex;
};

/**
 * Simple hash function to create a consistent identifier from a string.
 * Uses the FNV-1a hash algorithm for consistent, collision-resistant hashing.
 * This is a synchronous alternative to createHash for use cases like localStorage keys
 * where async operations are not suitable.
 *
 * @param value - String value to hash
 * @returns Hashed string
 */
export const createFNVHashSync = (value: string): string => {
  let hash = 2166136261; // FNV-1a offset basis (32-bit)
  const FNV_PRIME = 16777619; // FNV-1a prime (32-bit)

  for (let i = 0; i < value.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash ^= value.charCodeAt(i); // XOR with byte
    hash = Math.imul(hash, FNV_PRIME); // Multiply by FNV prime
  }

  // eslint-disable-next-line no-bitwise
  return (hash >>> 0).toString(16);
};
